import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { RequireAdmin } from './components/RequireAdmin';
import { RequireAuth } from './components/RequireAuth';
import { RequireSuperAdmin } from './components/RequireSuperAdmin';
import { RoleProvider } from './state/RoleContext';
import { AuthProvider } from './state/AuthContext';
import { ThemeProvider } from './state/ThemeContext';
import { BrandingProvider } from './state/BrandingContext';
import { paths } from './lib/paths';

import { ScoreGamePage } from './routes/ScoreGamePage';
import { LockerRoomPage } from './routes/LockerRoomPage';
import { StandsPage } from './routes/StandsPage';
import { FrontDeskPage } from './routes/FrontDeskPage';
import { CommunicationHubPage } from './routes/CommunicationHubPage';
import { SchedulerPage } from './routes/SchedulerPage';
import { DirectoryPage } from './routes/DirectoryPage';
import { DirectoryProfilePage } from './routes/DirectoryProfilePage';
import { EventCommunicationHistoryPage } from './routes/EventCommunicationHistoryPage';
import { TournamentsListPage } from './routes/TournamentsListPage';
import { TournamentCreatePage } from './routes/TournamentCreatePage';
import { TournamentSettingsPage } from './routes/TournamentSettingsPage';
import { RosterManagerPage } from './routes/RosterManagerPage';
import { OperationsPage } from './routes/OperationsPage';
import { NotFoundPage } from './routes/NotFoundPage';

import { CompanyHomePage } from './routes/CompanyHomePage';
import { CompanyDashboardPage } from './routes/CompanyDashboardPage';
import { CompanyCustomersPage } from './routes/CompanyCustomersPage';
import { CompanyContactsPage } from './routes/CompanyContactsPage';
import { CompanyMarketingPage } from './routes/CompanyMarketingPage';
import { CompanyPlaceholderPage } from './routes/CompanyPlaceholderPage';
import { TournamentWorkspacePage } from './routes/TournamentWorkspacePage';
import { TournamentOverviewPage } from './routes/TournamentOverviewPage';
import { TournamentScoringPage } from './routes/TournamentScoringPage';
import { TournamentWrapUpPage } from './routes/TournamentWrapUpPage';
import { EventLandingPage } from './routes/EventLandingPage';
import { EventIndexPage } from './routes/EventIndexPage';
import { EventMyTeamPage } from './routes/EventMyTeamPage';
import { EventMyStatsPage } from './routes/EventMyStatsPage';
import { EventMyGamesPage } from './routes/EventMyGamesPage';
import { EventStatsPage } from './routes/EventStatsPage';
import { PublicGamePage } from './routes/PublicGamePage';
import { PublicPlayerProfilePage } from './routes/PublicPlayerProfilePage';
import { PublicTeamProfilePage } from './routes/PublicTeamProfilePage';

import { MarketingHomePage } from './routes/MarketingHomePage';
import { AboutPage } from './routes/AboutPage';
import { ContactPage } from './routes/ContactPage';
import { ContactSubmissionsPage } from './routes/ContactSubmissionsPage';
import { PrivacyPolicyPage } from './routes/PrivacyPolicyPage';
import { TermsOfServicePage } from './routes/TermsOfServicePage';
import { SignInPage } from './routes/SignInPage';
import { MePage } from './routes/MePage';
import { PersonalInboxPage } from './routes/PersonalInboxPage';
import { ReportsPage } from './routes/ReportsPage';
import { SettingsPage } from './routes/SettingsPage';
import { RegistrationPage } from './routes/RegistrationPage';
import { RegistrationOverviewPage } from './routes/RegistrationOverviewPage';
import { BillingPage } from './routes/BillingPage';
import { LeagueLeaderboardPage } from './routes/LeagueLeaderboardPage';


/** Redirect helpers for old URL schemes. */
function RedirectEvent() {
  const { slug, '*': rest } = useParams();
  const tail = rest ? `/${rest}` : '';
  return <Navigate to={`/events/${slug}${tail}`} replace />;
}
function RedirectTournament() {
  const { id, '*': rest } = useParams();
  const tail = rest ? `/${rest}` : '';
  return <Navigate to={`/admin/tournaments/${id}${tail}`} replace />;
}
function RedirectScoreGame() {
  const { gameId } = useParams();
  return <Navigate to={`/admin/scoring/${gameId}`} replace />;
}

export function App() {
  return (
    <ThemeProvider>
      <RoleProvider>
        <AuthProvider>
          <BrandingProvider>
          <Routes>
          <Route element={<AppShell />}>
            {/* ============== PUBLIC: marketing ============== */}
            <Route index element={<MarketingHomePage />} />
            <Route path="about" element={<AboutPage />} />
            <Route path="contact" element={<ContactPage />} />
            <Route path="privacy" element={<PrivacyPolicyPage />} />
            <Route path="terms" element={<TermsOfServicePage />} />
            <Route path="sign-in" element={<SignInPage />} />

            {/* ============== PERSONAL HUB ============== */}
            <Route
              path="me"
              element={
                <RequireAuth>
                  <MePage />
                </RequireAuth>
              }
            />
            <Route
              path="me/inbox"
              element={
                <RequireAuth>
                  <PersonalInboxPage />
                </RequireAuth>
              }
            />

            {/* ============== SCOREKEEPER HQ: company internal ============== */}
            <Route path="company" element={<RequireSuperAdmin><CompanyDashboardPage /></RequireSuperAdmin>} />
            <Route path="company/leads" element={<RequireSuperAdmin><ContactSubmissionsPage /></RequireSuperAdmin>} />
            <Route path="company/contacts" element={<RequireSuperAdmin><CompanyContactsPage /></RequireSuperAdmin>} />
            <Route path="company/pipeline" element={<Navigate to={paths.companyPipeline} replace />} />
            <Route
              path="company/customers"
              element={
                <RequireSuperAdmin>
                  <CompanyCustomersPage />
                </RequireSuperAdmin>
              }
            />
            <Route path="company/pricing" element={<Navigate to={paths.companyCustomers} replace />} />
            <Route
              path="company/marketing"
              element={
                <RequireSuperAdmin>
                  <CompanyMarketingPage />
                </RequireSuperAdmin>
              }
            />
            <Route
              path="company/feedback"
              element={
                <RequireSuperAdmin>
                  <CompanyPlaceholderPage
                    eyebrow="Scorekeeper HQ"
                    title="Product feedback"
                    description="Partner league wishlist items, design feedback, feature requests, and roadmap ideas will live here as Scorekeeper HQ grows."
                  />
                </RequireSuperAdmin>
              }
            />

            {/* ============== PUBLIC: events ============== */}
            <Route path="events" element={<EventIndexPage />} />
            <Route path="register/:slug" element={<RegistrationPage />} />
            <Route path="leaderboards" element={<LeagueLeaderboardPage />} />
            <Route path="events/:slug" element={<EventLandingPage />}>
              <Route index element={<StandsPage />} />
              <Route path="schedule" element={<StandsPage />} />
              <Route path="stats" element={<EventStatsPage />} />
              <Route path="locker-room" element={<LockerRoomPage />} />
              <Route path="front-desk" element={<FrontDeskPage />} />
              <Route path="my-team" element={<EventMyTeamPage />} />
              <Route path="my-stats" element={<EventMyStatsPage />} />
              <Route path="my-games" element={<EventMyGamesPage />} />
              <Route path="my-games/:gameId/score" element={<RequireAuth><ScoreGamePage /></RequireAuth>} />
              <Route path="games/:gameId" element={<PublicGamePage />} />
              <Route path="players/:playerId" element={<PublicPlayerProfilePage />} />
              <Route path="teams/:teamId" element={<PublicTeamProfilePage />} />
            </Route>

            {/* ============== LEAGUE HQ ============== */}
            <Route
              path="admin"
              element={
                <RequireAdmin>
                  <CompanyHomePage />
                </RequireAdmin>
              }
            />
            <Route path="admin/directory" element={<RequireAdmin><DirectoryPage /></RequireAdmin>} />
            <Route path="admin/directory/:profileId" element={<RequireAdmin><DirectoryProfilePage /></RequireAdmin>} />
            <Route path="admin/comms" element={<RequireAdmin><CommunicationHubPage /></RequireAdmin>} />
            <Route path="admin/registration" element={<RequireAdmin><RegistrationOverviewPage /></RequireAdmin>} />
            <Route path="admin/game-operations" element={<Navigate to={paths.adminTournaments} replace />} />
            <Route path="admin/insights" element={<RequireAdmin><ReportsPage /></RequireAdmin>} />
            <Route path="admin/billing" element={<RequireAdmin><BillingPage /></RequireAdmin>} />
            <Route path="admin/reports" element={<Navigate to="/admin/insights" replace />} />
            <Route path="admin/settings" element={<RequireAdmin><SettingsPage /></RequireAdmin>} />
            <Route path="admin/contact" element={<Navigate to={paths.companyPipeline} replace />} />
            <Route path="admin/super" element={<Navigate to={paths.companyHome} replace />} />
            <Route path="admin/super/terms" element={<Navigate to={paths.companyCustomers} replace />} />
            <Route path="admin/super/contact" element={<Navigate to={paths.companyPipeline} replace />} />

            {/* Folded into other modules: playoffs live inside seasons,
                officials live in the directory, leaderboards live in insights. */}
            <Route path="admin/playoffs" element={<Navigate to="/admin/seasons" replace />} />
            <Route path="admin/officials" element={<Navigate to="/admin/directory" replace />} />
            <Route path="admin/leaderboards" element={<Navigate to="/admin/insights" replace />} />

            <Route path="admin/tournaments" element={<RequireAdmin><TournamentsListPage /></RequireAdmin>} />
            <Route path="admin/tournaments/new" element={<RequireAdmin><TournamentCreatePage /></RequireAdmin>} />
            <Route path="admin/seasons" element={<RequireAdmin><TournamentsListPage formatFilter="season" /></RequireAdmin>} />
            <Route path="admin/seasons/new" element={<RequireAdmin><TournamentCreatePage presetFormat="season" /></RequireAdmin>} />
            <Route
              path="admin/seasons/:id"
              element={<RequireAdmin><TournamentWorkspacePage /></RequireAdmin>}
            >
              <Route index element={<TournamentOverviewPage />} />
              <Route path="teams" element={<RosterManagerPage />} />
              <Route path="roster" element={<Navigate to="../teams" replace />} />
              <Route path="schedule" element={<SchedulerPage />} />
              <Route path="operations" element={<OperationsPage />} />
              <Route path="scoring" element={<TournamentScoringPage />} />
              <Route path="comms" element={<EventCommunicationHistoryPage />} />
              <Route path="wrap-up" element={<TournamentWrapUpPage />} />
            </Route>
            <Route
              path="admin/tournaments/:id"
              element={<RequireAdmin><TournamentWorkspacePage /></RequireAdmin>}
            >
              <Route index element={<TournamentOverviewPage />} />
              <Route path="teams" element={<RosterManagerPage />} />
              <Route path="roster" element={<Navigate to="../teams" replace />} />
              <Route path="schedule" element={<SchedulerPage />} />
              <Route path="operations" element={<OperationsPage />} />
              <Route path="scoring" element={<TournamentScoringPage />} />
              <Route path="comms" element={<EventCommunicationHistoryPage />} />
              <Route path="wrap-up" element={<TournamentWrapUpPage />} />
            </Route>
            <Route
              path="admin/tournaments/:id/settings"
              element={<RequireAdmin><TournamentSettingsPage /></RequireAdmin>}
            />
            <Route
              path="admin/seasons/:id/settings"
              element={<RequireAdmin><TournamentSettingsPage /></RequireAdmin>}
            />
            <Route
              path="admin/scoring/:gameId"
              element={<RequireAdmin><ScoreGamePage /></RequireAdmin>}
            />

            {/* ============== LEGACY REDIRECTS ============== */}
            <Route path="e" element={<Navigate to="/events" replace />} />
            <Route path="e/:slug/*" element={<RedirectEvent />} />
            <Route path="t/:id/*" element={<RedirectTournament />} />
            <Route path="tournaments" element={<Navigate to="/admin/tournaments" replace />} />
            <Route path="tournaments/new" element={<Navigate to="/admin/tournaments/new" replace />} />
            <Route path="tournaments/:id/*" element={<RedirectTournament />} />
            <Route path="directory" element={<Navigate to="/admin/directory" replace />} />
            <Route path="admin/people" element={<Navigate to="/admin/directory" replace />} />
            <Route path="communication-hub" element={<Navigate to="/admin/comms" replace />} />
            <Route path="commissioner" element={<Navigate to="/admin" replace />} />
            <Route path="scorekeeper" element={<Navigate to="/admin" replace />} />
            <Route path="scorekeeper/games/:gameId" element={<RedirectScoreGame />} />
            <Route path="captain" element={<Navigate to="/admin" replace />} />
            <Route path="player" element={<Navigate to="/admin" replace />} />
            <Route path="locker-room" element={<Navigate to="/events" replace />} />
            <Route path="stands" element={<Navigate to="/events" replace />} />
            <Route path="front-desk" element={<Navigate to="/events" replace />} />
            <Route path="scheduler" element={<Navigate to="/admin/tournaments" replace />} />
            <Route path="operations" element={<Navigate to="/admin/tournaments" replace />} />
            <Route path="pro-shop" element={<Navigate to="/" replace />} />
            <Route path="concessions" element={<Navigate to="/" replace />} />

            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
          </BrandingProvider>
        </AuthProvider>
      </RoleProvider>
    </ThemeProvider>
  );
}
